import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";
import { Base_Uri } from "../Constant/BaseUri";

const filter = createFilterOptions({
  stringify: (option) => {
    if (typeof option === "string") {
      return option;
    }

    return `${option?.label || ""} ${option?.value || ""}`;
  },
});

const getApiUrl = (path) => {
  const baseUrl = Base_Uri.endsWith("/")
    ? Base_Uri.slice(0, -1)
    : Base_Uri;

  const cleanPath = path.startsWith("/")
    ? path.slice(1)
    : path;

  return `${baseUrl}/${cleanPath}`;
};

const getStoredToken = () => {
  const savedData = sessionStorage.getItem("userData");

  if (!savedData) {
    return "";
  }

  try {
    const parsedData = JSON.parse(savedData);

    // When userData itself is only a token string.
    if (typeof parsedData === "string") {
      return parsedData;
    }

    // Change/add the correct property based on your login response.
    return (
      parsedData?.token ||
      parsedData?.accessToken ||
      parsedData?.authToken ||
      parsedData?.data?.token ||
      ""
    );
  } catch (error) {
    // When token was stored as a normal string, not JSON.
    return savedData;
  }
};

const SearchableCreatableDropdown = ({
  dropdownName,
  label,
  name,
  value,
  onChange,
  required = false,
  multiline = false,
  minRows = 1,
  placeholder = "",
}) => {
  const [options, setOptions] = useState([]);
  const [dropdownId, setDropdownId] = useState("");
  const [inputValue, setInputValue] = useState(value || "");
  const [loading, setLoading] = useState(false);
  const [addingOption, setAddingOption] = useState(false);
  const [deletingOptionId, setDeletingOptionId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const token = getStoredToken();

  const authConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    loadDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownName]);

  const updateFormValue = (newValue) => {
    if (typeof onChange !== "function") {
      return;
    }

    onChange({
      target: {
        name,
        value: newValue,
      },
    });
  };

  const normalizeOptions = (dropdownOptions = []) => {
    return dropdownOptions
      .filter((item) => item && item._id)
      .map((item) => ({
        _id: item._id,
        label: item.label,
        value: item.value,
      }));
  };

  const loadDropdown = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const response = await axios.get(
        getApiUrl(
          `dropdowns/name/${encodeURIComponent(dropdownName)}`
        ),
        authConfig
      );

      const dropdown = response?.data?.data;

      setDropdownId(dropdown?._id || "");
      setOptions(normalizeOptions(dropdown?.options));
    } catch (error) {
      if (error?.response?.status === 404) {
        await createDropdown();
      } else {
        const message =
          error?.response?.data?.message ||
          "Failed to load dropdown options.";

        setErrorMessage(message);

        console.error(
          "Get dropdown error:",
          error?.response?.data || error.message
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const createDropdown = async () => {
    try {
      setErrorMessage("");

      const response = await axios.post(
        getApiUrl("dropdowns"),
        {
          name: dropdownName,
          displayName: label,
          options: [],
        },
        authConfig
      );

      const dropdown = response?.data?.data;

      setDropdownId(dropdown?._id || "");
      setOptions(normalizeOptions(dropdown?.options));

      return dropdown?._id || "";
    } catch (error) {
      /*
       * The dropdown may have been created by another request.
       * Reload it if the server returns a duplicate error.
       */
      if (error?.response?.status === 409) {
        const response = await axios.get(
          getApiUrl(
            `dropdowns/name/${encodeURIComponent(dropdownName)}`
          ),
          authConfig
        );

        const dropdown = response?.data?.data;

        setDropdownId(dropdown?._id || "");
        setOptions(normalizeOptions(dropdown?.options));

        return dropdown?._id || "";
      }

      const message =
        error?.response?.data?.message ||
        "Failed to create dropdown.";

      setErrorMessage(message);

      console.error(
        "Create dropdown error:",
        error?.response?.data || error.message
      );

      return "";
    }
  };

  const ensureDropdownExists = async () => {
    if (dropdownId) {
      return dropdownId;
    }

    return await createDropdown();
  };

  const selectValue = (selectedValue) => {
    setInputValue(selectedValue);
    updateFormValue(selectedValue);
  };

  const addNewOption = async (newOptionValue) => {
    /*
     * Nothing is trimmed, lowercased, uppercased, or changed.
     */
    if (
      typeof newOptionValue !== "string" ||
      newOptionValue === ""
    ) {
      return;
    }

    const existingOption = options.find(
      (option) =>
        option.label === newOptionValue &&
        option.value === newOptionValue
    );

    if (existingOption) {
      selectValue(existingOption.value);
      return;
    }

    try {
      setAddingOption(true);
      setErrorMessage("");

      const currentDropdownId =
        await ensureDropdownExists();

      if (!currentDropdownId) {
        throw new Error("Dropdown ID could not be created.");
      }

      const response = await axios.post(
        getApiUrl(
          `dropdowns/${currentDropdownId}/options`
        ),
        {
          label: newOptionValue,
          value: newOptionValue,
        },
        authConfig
      );

      /*
       * Supports these backend response formats:
       *
       * data: addedOption
       *
       * or:
       *
       * data: {
       *   option: addedOption
       * }
       */
      const responseData = response?.data?.data;

      let addedOption =
        responseData?.option || responseData;

      /*
       * The API must return the new option with its MongoDB _id.
       * Reload when the response does not contain the ID.
       */
      if (!addedOption?._id) {
        const reloadResponse = await axios.get(
          getApiUrl(
            `dropdowns/name/${encodeURIComponent(
              dropdownName
            )}`
          ),
          authConfig
        );

        const reloadedDropdown =
          reloadResponse?.data?.data;

        const reloadedOptions = normalizeOptions(
          reloadedDropdown?.options
        );

        setDropdownId(reloadedDropdown?._id || "");
        setOptions(reloadedOptions);

        addedOption = reloadedOptions.find(
          (option) =>
            option.label === newOptionValue &&
            option.value === newOptionValue
        );
      } else {
        const formattedOption = {
          _id: addedOption._id,
          label: addedOption.label,
          value: addedOption.value,
        };

        setOptions((previousOptions) => {
          const alreadyExists = previousOptions.some(
            (option) =>
              option._id === formattedOption._id ||
              (
                option.label === formattedOption.label &&
                option.value === formattedOption.value
              )
          );

          if (alreadyExists) {
            return previousOptions;
          }

          return [
            ...previousOptions,
            formattedOption,
          ];
        });
      }

      selectValue(newOptionValue);
    } catch (error) {
      if (error?.response?.status === 409) {
        await loadDropdown();
        selectValue(newOptionValue);
      } else {
        const message =
          error?.response?.data?.message ||
          error.message ||
          "Failed to add option.";

        setErrorMessage(message);

        console.error(
          "Add option error:",
          error?.response?.data || error.message
        );
      }
    } finally {
      setAddingOption(false);
    }
  };

  const deleteOption = async (event, option) => {
    /*
     * Prevent selecting the option when clicking delete.
     */
    event.preventDefault();
    event.stopPropagation();

    if (!dropdownId || !option?._id) {
      setErrorMessage(
        "Dropdown ID or option ID is missing."
      );
      return;
    }

    try {
      setDeletingOptionId(option._id);
      setErrorMessage("");

      await axios.delete(
        getApiUrl(
          `dropdowns/${dropdownId}/options/${option._id}`
        ),
        authConfig
      );

      setOptions((previousOptions) =>
        previousOptions.filter(
          (item) => item._id !== option._id
        )
      );

      /*
       * Clear the form when the currently selected option
       * is deleted.
       */
      if (value === option.value) {
        selectValue("");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Failed to delete option.";

      setErrorMessage(message);

      console.error(
        "Delete option error:",
        error?.response?.data || error.message
      );
    } finally {
      setDeletingOptionId("");
    }
  };

  const handleAutocompleteChange = async (
    event,
    newValue
  ) => {
    if (newValue === null) {
      selectValue("");
      return;
    }

    /*
     * User typed a value and pressed Enter.
     */
    if (typeof newValue === "string") {
      await addNewOption(newValue);
      return;
    }

    /*
     * User selected the "Add new option" row.
     */
    if (newValue?.isNewOption) {
      await addNewOption(newValue.inputValue);
      return;
    }

    /*
     * User selected an existing option.
     */
    selectValue(newValue.value);
  };

  return (
    <Autocomplete
      fullWidth
      freeSolo
      selectOnFocus
      clearOnBlur={false}
      handleHomeEndKeys
      loading={loading}
      options={options}
      value={value || null}
      inputValue={inputValue}
      getOptionKey={(option) => {
        if (typeof option === "string") {
          return option;
        }

        return (
          option?._id ||
          `new-${option?.inputValue || option?.value}`
        );
      }}
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        }

        if (option?.inputValue !== undefined) {
          return option.inputValue;
        }

        return option?.label || "";
      }}
      isOptionEqualToValue={(option, selectedValue) => {
        if (typeof selectedValue === "string") {
          return option.value === selectedValue;
        }

        return (
          option._id === selectedValue?._id ||
          (
            option.label === selectedValue?.label &&
            option.value === selectedValue?.value
          )
        );
      }}
      filterOptions={(availableOptions, params) => {
        const filteredOptions = filter(
          availableOptions,
          params
        );

        const typedValue = params.inputValue;

        const exactOptionExists =
          availableOptions.some(
            (option) =>
              option.label === typedValue &&
              option.value === typedValue
          );

        /*
         * Show a separate add option row.
         * The entered text is not modified.
         */
        if (
          typedValue !== "" &&
          !exactOptionExists
        ) {
          filteredOptions.push({
            inputValue: typedValue,
            label: `Add "${typedValue}"`,
            value: typedValue,
            isNewOption: true,
          });
        }

        return filteredOptions;
      }}
      onInputChange={(
        event,
        newInputValue,
        reason
      ) => {
        setInputValue(newInputValue);

        /*
         * Keep form state synchronized with typed text.
         */
        if (
          reason === "input" ||
          reason === "clear"
        ) {
          updateFormValue(newInputValue);
        }
      }}
      onChange={handleAutocompleteChange}
      noOptionsText={
        inputValue
          ? `Select "Add ${inputValue}"`
          : "No options available"
      }
      renderOption={(props, option) => {
        if (option.isNewOption) {
          return (
            <li
              {...props}
              key={`add-${option.inputValue}`}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                }}
              >
                <AddIcon fontSize="small" />

                <Typography>
                  Add new option:{" "}
                  <strong>
                    {option.inputValue}
                  </strong>
                </Typography>
              </Box>
            </li>
          );
        }

        return (
          <li
            {...props}
            key={option._id}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {option.label}
              </Typography>

              <IconButton
                component="span"
                size="small"
                disabled={
                  deletingOptionId === option._id
                }
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) =>
                  deleteOption(event, option)
                }
                aria-label={`Delete ${option.label}`}
              >
                {deletingOptionId === option._id ? (
                  <CircularProgress size={17} />
                ) : (
                  <DeleteOutlineIcon
                    fontSize="small"
                  />
                )}
              </IconButton>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          multiline={multiline}
          minRows={
            multiline ? minRows : undefined
          }
          label={label}
          name={name}
          placeholder={placeholder}
          error={Boolean(errorMessage)}
          helperText={
            errorMessage ||
            "Search, select, add, or delete an option."
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(loading || addingOption) && (
                  <CircularProgress
                    size={20}
                    sx={{ mr: 1 }}
                  />
                )}

                {
                  params.InputProps
                    .endAdornment
                }
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SearchableCreatableDropdown;